import React, { useEffect, useRef, useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { ApiTrackDto } from '../services/trackApiService';
import { trackStore, hasGuestCustomTrack, LoginRequiredError } from '../services/trackStore';

type Mode = 'export' | 'import';

interface ImportExportModalProps {
  show: boolean;
  mode: Mode;
  onHide: () => void;
  isAuthenticated: boolean;
  onImported?: () => void;
}

const EXPORT_FILENAME = 'speed-trainer-tracks.json';

const ImportExportModal: React.FC<ImportExportModalProps> = ({
  show, mode, onHide, isAuthenticated, onImported,
}) => {
  // ── Export state ─────────────────────────────────────────────────────────
  const [allTracks, setAllTracks] = useState<ApiTrackDto[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Import state ─────────────────────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [importTracks, setImportTracks] = useState<ApiTrackDto[] | null>(null);
  const [importFileName, setImportFileName] = useState<string>('');
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);

  // Reset on open
  useEffect(() => {
    if (!show) return;
    setError(null);
    setImportResult(null);
    if (mode === 'export') {
      setLoading(true);
      trackStore.getAll()
        .then((tracks) => {
          const sorted = [...tracks].sort((a, b) => a.name.localeCompare(b.name));
          setAllTracks(sorted);
          setSelectedIds(new Set(sorted.map((t) => t.id)));
        })
        .catch((e: Error) => setError(`Could not load tracks: ${e.message}`))
        .finally(() => setLoading(false));
    } else {
      setImportTracks(null);
      setImportFileName('');
    }
  }, [show, mode]);

  // ── Export handlers ──────────────────────────────────────────────────────

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(allTracks.map((t) => t.id)));
  const selectNone = () => setSelectedIds(new Set());

  const handleExport = () => {
    const tracksToExport = allTracks
      .filter((t) => selectedIds.has(t.id))
      .map(({ id, ...rest }) => rest);

    const payload = {
      version: 1,
      exportedAt: new Date().toISOString(),
      tracks: tracksToExport,
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = EXPORT_FILENAME;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    onHide();
  };

  // ── Import handlers ──────────────────────────────────────────────────────

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setImportTracks(null);
    setImportResult(null);
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFileName(file.name);
    file.text()
      .then((text) => {
        const parsed = JSON.parse(text);
        const tracks: any[] = Array.isArray(parsed) ? parsed : parsed.tracks;
        if (!Array.isArray(tracks)) {
          throw new Error('File does not contain a tracks array.');
        }
        // Minimal validation
        const valid = tracks.filter((t) =>
          t && typeof t.name === 'string' && Array.isArray(t.parts) && Array.isArray(t.links),
        );
        if (valid.length === 0) {
          throw new Error('No valid tracks found in file.');
        }
        setImportTracks(valid as ApiTrackDto[]);
      })
      .catch((err: Error) => {
        setError(`Could not read file: ${err.message}`);
      });
  };

  const handleImport = async () => {
    if (!importTracks) return;
    setImportBusy(true);
    setError(null);
    setImportResult(null);

    try {
      let toImport = importTracks;

      if (!isAuthenticated) {
        if (hasGuestCustomTrack()) {
          setError('You already have a custom track. Sign in to import additional tracks, or delete your existing custom track first.');
          setImportBusy(false);
          return;
        }
        toImport = importTracks.slice(0, 1);
      }

      let created = 0;
      const failures: string[] = [];

      for (const t of toImport) {
        try {
          await trackStore.create({
            name: t.name,
            bpm: t.bpm ?? 120,
            defaultPitchShift: t.defaultPitchShift ?? 0,
            youtubeId: t.youtubeId ?? null,
            audioFile: t.audioFile ?? null,
            parts: t.parts ?? [],
            links: t.links ?? [],
          });
          created++;
        } catch (e: any) {
          if (e instanceof LoginRequiredError) {
            failures.push(`${t.name}: sign-in required`);
            break;
          }
          failures.push(`${t.name}: ${e.message}`);
        }
      }

      const skipped = importTracks.length - toImport.length;
      const parts: string[] = [`Imported ${created} track${created === 1 ? '' : 's'}.`];
      if (skipped > 0) parts.push(`${skipped} skipped (sign in to import all).`);
      if (failures.length) parts.push(`Failed: ${failures.join('; ')}`);
      setImportResult(parts.join(' '));

      if (created > 0) {
        onImported?.();
        if (failures.length === 0) onHide();
      }
    } finally {
      setImportBusy(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{mode === 'export' ? 'Export Tracks' : 'Import Tracks'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger" onClose={() => setError(null)} dismissible>{error}</Alert>}

        {mode === 'export' && (
          <>
            {loading ? (
              <div className="d-flex align-items-center gap-2">
                <Spinner size="sm" /> <span className="text-muted">Loading tracks…</span>
              </div>
            ) : (
              <>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small">
                    {selectedIds.size} of {allTracks.length} selected
                  </span>
                  <div className="d-flex gap-2">
                    <Button size="sm" variant="outline-secondary" onClick={selectAll}>All</Button>
                    <Button size="sm" variant="outline-secondary" onClick={selectNone}>None</Button>
                  </div>
                </div>
                <div style={{ maxHeight: 360, overflowY: 'auto' }} className="border rounded p-2">
                  {allTracks.length === 0 ? (
                    <p className="text-muted small mb-0">No tracks available.</p>
                  ) : allTracks.map((t) => (
                    <Form.Check
                      key={t.id}
                      type="checkbox"
                      id={`export-${t.id}`}
                      checked={selectedIds.has(t.id)}
                      onChange={() => toggleSelect(t.id)}
                      label={
                        <span>
                          {t.name}{' '}
                          <span className="text-muted small">
                            ({t.parts.length} part{t.parts.length === 1 ? '' : 's'} · {t.bpm} BPM)
                          </span>
                        </span>
                      }
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {mode === 'import' && (
          <>
            {!isAuthenticated && (
              <Alert variant="warning" className="small">
                <strong>You are not signed in.</strong> Only the first track in the file will be
                imported. If you already have a custom track, no tracks will be imported.
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Choose an export file</Form.Label>
              <Form.Control
                ref={fileInputRef}
                type="file"
                accept="application/json,.json"
                onChange={handleFilePick}
              />
            </Form.Group>

            {importTracks && (
              <div className="mb-2">
                <div className="text-muted small mb-2">
                  <strong>{importFileName}</strong> — {importTracks.length} track{importTracks.length === 1 ? '' : 's'} found
                  {!isAuthenticated && importTracks.length > 1 && (
                    <Badge bg="warning" text="dark" className="ms-2">
                      Only first will be imported
                    </Badge>
                  )}
                </div>
                <div style={{ maxHeight: 240, overflowY: 'auto' }} className="border rounded p-2">
                  <ul className="small mb-0 ps-3">
                    {importTracks.map((t, i) => (
                      <li key={i} className={!isAuthenticated && i > 0 ? 'text-muted' : ''}>
                        {t.name}
                        <span className="text-muted"> ({t.parts.length} parts · {t.bpm} BPM)</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {importResult && (
              <Alert variant="info" className="small mt-2 mb-0">{importResult}</Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
        {mode === 'export' && (
          <Button
            variant="primary"
            onClick={handleExport}
            disabled={loading || selectedIds.size === 0}
          >
            Download JSON
          </Button>
        )}
        {mode === 'import' && (
          <Button
            variant="primary"
            onClick={handleImport}
            disabled={!importTracks || importBusy}
          >
            {importBusy ? <><Spinner size="sm" className="me-2" /> Importing…</> : 'Import'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ImportExportModal;
